/* eslint-disable react/display-name */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { LoadingDots } from '@app/ui';

import BoundingBox from './BoundingBox';
import SelectionBox from './SelectionBox';
import EditBox from './EditBox';
import './boxes.scss';
import { getImageTransformations } from '../../utils/boundingBoxes';

const MIN_SIZE = 10;

const BoxDrawer = ({
    imageUrl,
    allowBoundingBox: allowBB,
    boxes,
    postLink,
    update,
    create,
    remove,
    disableEdit = false,
    setClientImage,
    initBB,
    hideBoxes,
}) => {
    const canvas = useRef(null);
    const [isSelecting, setSelecting] = useState(false);
    const [editId, setEditId] = useState(null);
    const [selectionBox, setSelectionBox] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageNotLoaded, setImageNotLoaded] = useState(false);
    const [showLoadingDots, setShowLoadingDots] = useState(false);
    const allowBoundingBox = !imageLoading && allowBB;

    useEffect(() => {
        setEditId(null);
    }, [imageUrl]);

    useEffect(() => {
        if (!imageLoading || imageNotLoaded) {
            setShowLoadingDots(false);
            return;
        }
        const showLoadingTimeout = setTimeout(
            () => setShowLoadingDots(true),
            1000,
        );
        return () => {
            clearTimeout(showLoadingTimeout);
        };
    }, [imageLoading, imageNotLoaded]);

    useEffect(() => {
        if (!canvas.current) return;
        if (!imageUrl) return;
        setShowLoadingDots(false);
        setImageLoading(true);
        setImageNotLoaded(false);
        const image = new Image();
        image.src = imageUrl;
        const loadListener = image.addEventListener('load', () => {
            canvas.current.src = imageUrl;
            const clientImage = getImageTransformations(canvas.current);
            initBB(clientImage);
            setImageLoading(false);
        });
        const errorListener = image.addEventListener('error', () =>
            setImageNotLoaded(true),
        );
        return () => {
            image.removeEventListener('load', loadListener);
            image.removeEventListener('error', errorListener);
        };
    }, [imageUrl, canvas.current]);

    const getCursorPosition = e => {
        const { top, left } = canvas.current.getBoundingClientRect();
        return [e.clientX - left, e.clientY - top];
    };

    const onMouseDown = e => {
        if (!allowBoundingBox) return;

        const [x, y] = getCursorPosition(e);
        setSelecting(true);
        setSelectionBox({
            mouse: [x, y],
            start: [x, y],
            end: [0, 0],
        });
    };

    const onMouseMove = e => {
        e.stopPropagation();
        if (!isSelecting) return;
        const [x, y] = getCursorPosition(e);
        const [startX, startY] = selectionBox.mouse;
        setSelectionBox({
            ...selectionBox,
            start: [Math.min(startX, x), Math.min(startY, y)],
            end: [Math.abs(startX - x), Math.abs(startY - y)],
        });
    };

    const onMouseUp = e => {
        e.stopPropagation();
        if (!allowBoundingBox || !isSelecting || !selectionBox) return;

        const { ...box } = selectionBox;
        const [x, y] = selectionBox.end;
        const isTooSmall = x < MIN_SIZE || y < MIN_SIZE;

        setSelecting(false);
        setSelectionBox(null);

        // If annotation is too small, throw it as well
        if (isTooSmall) return;

        create({ box });
    };

    const _setEdit = id => () => {
        setEditId(id);
    };

    const _editUpdate = tempId => async box => {
        await update({ tempId, box });
    };

    const _remove = tempId => async () => {
        setEditId(null);
        remove({ tempId });
    };

    useLayoutEffect(() => {
        function updateSize() {
            setClientImage(getImageTransformations(canvas.current));
        }
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    const showBoxes =
        !imageNotLoaded &&
        !imageLoading &&
        canvas.current &&
        canvas.current.src &&
        !hideBoxes;
    return (
        <div className="gds-lens-wrapper" style={{ width: '100%' }}>
            {showLoadingDots && (
                <div
                    style={{
                        width: '200px',
                        height: '100px',
                        color: '#fff',
                    }}
                >
                    <p>Image is loading...</p>
                    <LoadingDots context="white" size="lg" />
                </div>
            )}
            {imageNotLoaded && (
                <div
                    style={{
                        maxWidth: '600px',
                        height: '100px',
                        color: '#fff',
                    }}
                >
                    <p>
                        Image cannot be loaded.
                        <br />
                        Please report this post url and mark this post as
                        &quot;No Rightsholders&quot;.
                    </p>
                    <p>
                        <a
                            className="gds-text--primary"
                            href={postLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {postLink}
                        </a>
                    </p>
                </div>
            )}
            {!imageNotLoaded && (
                <img
                    ref={canvas}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseOut={onMouseUp}
                    draggable={false}
                    style={{
                        width: '100%',
                        height: 'auto',
                    }}
                    className="gds-lens-base annotation-image-v2 image-annotation"
                />
            )}

            {isSelecting && !disableEdit && (
                <SelectionBox
                    start={selectionBox.start}
                    end={selectionBox.end}
                />
            )}
            {showBoxes &&
                boxes.map(b => {
                    const { start, end, tempId } = b;
                    const onClick = _setEdit(tempId);
                    const updateBB = _editUpdate(tempId);
                    const removeBB = _remove(tempId);

                    const isEditting = editId === tempId && !disableEdit;
                    const imageUrl =
                        canvas.current && canvas.current.src
                            ? canvas.current.src
                            : null;
                    const { offsetWidth: width, offsetHeight: height } =
                        canvas.current;
                    return (
                        <React.Fragment key={tempId}>
                            {isEditting && (
                                <EditBox
                                    start={start}
                                    end={end}
                                    imageUrl={imageUrl}
                                    update={updateBB}
                                    remove={removeBB}
                                    getCursorPosition={getCursorPosition}
                                    width={width}
                                    height={height}
                                />
                            )}
                            {!isEditting && (
                                <BoundingBox
                                    start={start}
                                    end={end}
                                    noEvents={isSelecting}
                                    imageUrl={imageUrl}
                                    onClick={onClick}
                                    width={width}
                                    height={height}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
        </div>
    );
};

BoxDrawer.propTypes = {
    boxes: PropTypes.array,
    imageUrl: PropTypes.string,
    create: PropTypes.func,
    update: PropTypes.func,
    remove: PropTypes.func,
    allowBoundingBox: PropTypes.bool,
    postLink: PropTypes.string,
    disableEdit: PropTypes.bool,
    setClientImage: PropTypes.func,
    initBB: PropTypes.func,
    hideBoxes: PropTypes.bool,
};

export default BoxDrawer;
